import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiFetchMultipart } from '@/lib/api';
import {
  IMPORT_MAX_ERRORS,
  IMPORT_MAX_FILE_BYTES,
  IMPORT_MAX_ROWS,
  summarizeImport,
} from '@/lib/csv-import';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { AttendeeImportResult } from '@tunodo/ticketing-shared';
import type { AttendeeImportResult as AttendeeImportResultType } from '@tunodo/ticketing-shared';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  FileUp,
  Loader2,
  UploadCloud,
  XCircle,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_app/attendees/import')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden importar inscritos.');
      throw redirect({ to: '/' });
    }
  },
  component: AttendeeImportPage,
});

type Step = 'upload' | 'review' | 'confirming';

const MAX_FILE_SIZE = IMPORT_MAX_FILE_BYTES;
const MAX_ERRORS_VISIBLE = IMPORT_MAX_ERRORS;

function AttendeeImportPage() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [dryRunResult, setDryRunResult] = useState<AttendeeImportResultType | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Mutación dryRun ──────────────────────────────────────────────────────────
  const dryRunMutation = useMutation({
    mutationKey: ['attendees', 'import', 'dryRun'],
    mutationFn: async (f: File) => {
      const formData = new FormData();
      formData.append('file', f);
      formData.append('dryRun', 'true');
      return apiFetchMultipart(
        '/api/attendees/import-csv?dryRun=true',
        formData,
        AttendeeImportResult,
      );
    },
    onSuccess: (result) => {
      setDryRunResult(result);
      setApiError(null);
      setStep('review');
    },
    onError: (err) => {
      setApiError(err instanceof Error ? err.message : 'Error desconocido');
    },
  });

  // ── Mutación confirm ─────────────────────────────────────────────────────────
  const confirmMutation = useMutation({
    mutationKey: ['attendees', 'import', 'confirm'],
    mutationFn: async (f: File) => {
      const formData = new FormData();
      formData.append('file', f);
      formData.append('dryRun', 'false');
      return apiFetchMultipart(
        '/api/attendees/import-csv?dryRun=false',
        formData,
        AttendeeImportResult,
      );
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['attendees'] });
      toast.success(
        `${result.created} inscrito${result.created !== 1 ? 's' : ''} creado${result.created !== 1 ? 's' : ''} correctamente.`,
      );
      void router.navigate({ to: '/attendees', search: { page: 1, pageSize: 10 } });
    },
    onError: (err) => {
      setApiError(err instanceof Error ? err.message : 'Error desconocido');
      setStep('review');
    },
  });

  // ── Validación local ─────────────────────────────────────────────────────────
  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setApiError(null);
  }

  function handleValidate() {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('El archivo debe ser un CSV (.csv).');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('El archivo supera el límite de 5 MB.');
      return;
    }

    dryRunMutation.mutate(file);
  }

  function handleConfirm() {
    if (!file) return;
    setStep('confirming');
    setApiError(null);
    confirmMutation.mutate(file);
  }

  function handleReset() {
    setFile(null);
    setDryRunResult(null);
    setApiError(null);
    setStep('upload');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const isValidating = dryRunMutation.isPending;
  const isConfirming = confirmMutation.isPending || step === 'confirming';

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <main className="flex-1 p-6 space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Importar CSV</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Carga un archivo CSV para registrar inscritos al evento.
        </p>
      </div>

      {/* Error de API */}
      {apiError && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{apiError}</AlertDescription>
        </Alert>
      )}

      {/* ── Paso 1: Selección de archivo ──────────────────────────────────── */}
      {(step === 'upload' || isValidating) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UploadCloud className="h-5 w-5" />
              Seleccionar archivo
            </CardTitle>
            <CardDescription>
              El CSV debe contener exactamente las columnas{' '}
              <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">
                nombre_completo
              </code>
              , <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">email</code> y{' '}
              <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">telefono</code> (en
              ese orden, obligatorias). Codificación UTF-8 o Latin-1. Máximo 5 MB y{' '}
              {IMPORT_MAX_ROWS} filas.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="csv-file">Archivo CSV</Label>
              <input
                ref={fileInputRef}
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={isValidating}
                className="block w-full text-sm text-muted-foreground
                  file:mr-4 file:py-2 file:px-4 file:rounded-md
                  file:border file:border-input file:bg-background
                  file:text-sm file:font-medium file:cursor-pointer
                  hover:file:bg-accent hover:file:text-accent-foreground
                  disabled:opacity-50"
              />
              {file && (
                <p className="text-xs text-muted-foreground">
                  {file.name} — {(file.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>
            <Button onClick={handleValidate} disabled={!file || isValidating} className="gap-2">
              {isValidating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Validando…
                </>
              ) : (
                <>
                  <FileUp className="h-4 w-4" />
                  Validar
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Paso 2/3: Resumen del dryRun ──────────────────────────────────── */}
      {(step === 'review' || step === 'confirming') &&
        dryRunResult !== null &&
        (() => {
          // `total` de Go cuenta solo candidatos validos y unicos, y `errors` llega cortado en 50.
          const summary = summarizeImport(dryRunResult);
          const { toCreate: newCount, rowErrors, errorsTruncated } = summary;
          const dupCount = summary.existing + summary.csvDuplicates;
          const errorCount = rowErrors.length;
          const partial = errorsTruncated ? '+' : '';
          const visibleErrors = rowErrors.slice(0, MAX_ERRORS_VISIBLE);
          const hiddenCount = rowErrors.length - visibleErrors.length;

          return (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Resumen de validación
                </CardTitle>
                <CardDescription>
                  Archivo: <span className="font-medium">{file?.name}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Métricas */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-lg border bg-card p-4 text-center space-y-1">
                    <p className="text-3xl font-bold text-green-600">{newCount}</p>
                    <p className="text-xs text-muted-foreground">Registros nuevos</p>
                  </div>
                  <div className="rounded-lg border bg-card p-4 text-center space-y-1">
                    <p className="text-3xl font-bold text-yellow-600">
                      {dupCount}
                      {partial}
                    </p>
                    <p className="text-xs text-muted-foreground">Duplicados</p>
                  </div>
                  <div className="rounded-lg border bg-card p-4 text-center space-y-1">
                    <p className="text-3xl font-bold text-destructive">
                      {errorCount}
                      {partial}
                    </p>
                    <p className="text-xs text-muted-foreground">Errores de fila</p>
                  </div>
                </div>

                {errorsTruncated && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Lista de errores recortada</AlertTitle>
                    <AlertDescription>
                      La API devuelve como máximo {IMPORT_MAX_ERRORS} errores. Los conteos de
                      duplicados dentro del CSV y de errores de fila pueden ser mayores a los
                      mostrados.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Tabla de errores de fila */}
                {rowErrors.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Errores por fila</p>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">Fila</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Mensaje</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visibleErrors.map((err) => (
                            <TableRow key={`${err.row}-${err.reason}`}>
                              <TableCell className="font-mono text-xs">{err.row}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {typeof err.data?.email === 'string' ? err.data.email : '—'}
                              </TableCell>
                              <TableCell className="text-xs text-destructive">
                                {err.reason}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {hiddenCount > 0 && (
                      <p className="text-xs text-muted-foreground text-right">
                        y {hiddenCount} error{hiddenCount !== 1 ? 'es' : ''} más…
                      </p>
                    )}
                  </div>
                )}

                {/* Estado: nada por importar */}
                {newCount === 0 && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Nada por importar</AlertTitle>
                    <AlertDescription>
                      No hay registros nuevos para crear. Todos son duplicados o contienen errores.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Estado: hay errores pero hay nuevos */}
                {newCount > 0 && errorCount > 0 && (
                  <Alert className="border-yellow-500/50 text-yellow-700 [&>svg]:text-yellow-600">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Revisa los errores</AlertTitle>
                    <AlertDescription>
                      Puedes confirmar de todas formas para crear solo los{' '}
                      <strong>{newCount}</strong> registro{newCount !== 1 ? 's' : ''} válido
                      {newCount !== 1 ? 's' : ''}. Las filas con error serán ignoradas.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Acciones */}
                <div className="flex items-center gap-3 flex-wrap">
                  {newCount > 0 && (
                    <Button
                      onClick={handleConfirm}
                      disabled={isConfirming}
                      className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                    >
                      {isConfirming ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Importando…
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4" />
                          Confirmar import
                        </>
                      )}
                    </Button>
                  )}

                  {(errorCount > 0 || newCount === 0) && (
                    <Button
                      variant="outline"
                      onClick={handleReset}
                      disabled={isConfirming}
                      className="gap-2"
                    >
                      <XCircle className="h-4 w-4" />
                      Cancelar y subir otro CSV
                    </Button>
                  )}

                  {newCount === 0 && (
                    <Badge variant="secondary" className="text-xs">
                      Sin cambios
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })()}
    </main>
  );
}
